import { useEffect, useMemo, useRef } from 'react';
import { z } from 'zod';
import { differenceInCalendarDays, format } from 'date-fns';
import { useLingui } from '@lingui/react';

import { BaseEditableForm } from '@/components/form/BaseEditableForm';
import type { FieldConfig, GroupConfig, EnhancedSelectOption } from '@/components/form/types';
import { parseDateString } from '@/utils/dateUtils';
import { useCreateChangeRequest } from '@/hooks/useChildDataUpdateRequest';
import {
  PAUSE_SUB_TYPES,
  buildPauseBatch,
  getSubTypeLabel,
  type PauseRequestFormValues,
  type ChangeRequestSourceKind,
} from '@/types/changeRequest';
import type { change_requests_ChangeRequestResultDTO } from '@/api';
import { typeError } from '@/lib/zodTypeError';

interface PauseRequestFormProps {
  /** The contract group the pause targets (from the Contract editor). */
  contractGroupId: string;
  /** Optional explicit target version; when absent the executor resolves the covering version. */
  versionId?: string;
  /** Contract active window — dates must fall within [from, to] when both are provided. */
  activeRangeFrom?: string;
  activeRangeTo?: string;
  /** Producer source — only the kind differs across channels; defaults to web_admin. */
  source?: { kind: ChangeRequestSourceKind; message_id?: string };
  /** Receives the per-request result after a successful create. */
  onResult?: (result: change_requests_ChangeRequestResultDTO | undefined) => void;
  onSuccess?: () => void;
  onCancel?: () => void;
}

type FormValues = PauseRequestFormValues;

/**
 * Create a `pause` (create / extend / cancel) change request from the Contract
 * editor. Mirrors {@link CareDayRequestForm}: built on BaseEditableForm, posts
 * through the producer-agnostic {@link useCreateChangeRequest} hook, and lands in
 * triage as `family='pause'` with the exact PauseChangeSpec `executor.body`.
 */
export function PauseRequestForm({
  contractGroupId,
  versionId,
  activeRangeFrom,
  activeRangeTo,
  source,
  onResult,
  onSuccess,
  onCancel,
}: PauseRequestFormProps) {
  const { i18n } = useLingui();
  const containerRef = useRef<HTMLDivElement>(null);

  const create = useCreateChangeRequest();

  const schema = useMemo(
    () =>
      z
        .object({
          subType: z.enum(PAUSE_SUB_TYPES),
          pauseFrom: z.date({
            error: typeError({
              required: i18n._({ id: 'changeRequest.validation.required', message: 'Required' }),
              invalid: i18n._({ id: 'changeRequest.validation.required', message: 'Required' }),
            }),
          }),
          pauseTo: z.date({
            error: typeError({
              required: i18n._({ id: 'changeRequest.validation.required', message: 'Required' }),
              invalid: i18n._({ id: 'changeRequest.validation.required', message: 'Required' }),
            }),
          }),
          reason: z.string().optional().default(''),
        })
        .superRefine((val, ctx) => {
          if (val.pauseFrom && val.pauseTo && val.pauseTo.getTime() < val.pauseFrom.getTime()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['pauseTo'],
              message: i18n._({
                id: 'changeRequest.validation.windowOrder',
                message: 'The end date must be on or after the start date',
              }),
            });
          }
          const rangeFrom = parseDateString(activeRangeFrom);
          const rangeTo = parseDateString(activeRangeTo);
          const withinMessage = i18n._({
            id: 'changeRequest.validation.withinContractRange',
            message: "The dates must fall within the contract's active range",
          });
          if (rangeFrom && val.pauseFrom && differenceInCalendarDays(val.pauseFrom, rangeFrom) < 0) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['pauseFrom'], message: withinMessage });
          }
          if (rangeTo && val.pauseTo && differenceInCalendarDays(val.pauseTo, rangeTo) > 0) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['pauseTo'], message: withinMessage });
          }
        }),
    [i18n, activeRangeFrom, activeRangeTo],
  ) as unknown as z.ZodType<FormValues, FormValues>;

  const fields: FieldConfig<FormValues>[] = useMemo(() => {
    const subTypeOptions: EnhancedSelectOption[] = PAUSE_SUB_TYPES.map((st) => ({
      value: st,
      label: getSubTypeLabel(i18n, st),
    }));

    return [
      {
        name: 'subType',
        label: i18n._({ id: 'changeRequest.field.subType', message: 'Change type' }),
        type: 'select',
        required: true,
        selectOptions: subTypeOptions,
      },
      {
        name: 'pauseFrom',
        label: i18n._({ id: 'changeRequest.field.pauseFrom', message: 'Pause from' }),
        type: 'date',
        required: true,
      },
      {
        name: 'pauseTo',
        label: i18n._({ id: 'changeRequest.field.pauseTo', message: 'Pause to' }),
        type: 'date',
        required: true,
      },
      {
        name: 'reason',
        label: i18n._({ id: 'changeRequest.field.reason', message: 'Reason (optional)' }),
        type: 'textarea',
        colSpan: 2,
      },
    ];
  }, [i18n]);

  const groups: GroupConfig<FormValues>[] = useMemo(
    () => [
      {
        title: i18n._({ id: 'changeRequest.group.pause', message: 'Pause change' }),
        fields: ['subType', 'pauseFrom', 'pauseTo', 'reason'] as Array<keyof FormValues>,
      },
    ],
    [i18n],
  );

  const defaultValues: Partial<FormValues> = {
    subType: 'create',
    pauseFrom: undefined,
    pauseTo: undefined,
    reason: '',
  };

  const handleSubmit = async (values: FormValues) => {
    const batch = buildPauseBatch({
      subType: values.subType,
      contractGroupId,
      versionId,
      pauseFrom: values.pauseFrom ? format(values.pauseFrom, 'yyyy-MM-dd') : '',
      pauseTo: values.pauseTo ? format(values.pauseTo, 'yyyy-MM-dd') : '',
      reason: values.reason,
      source: source ?? { kind: 'web_admin' },
    });

    const res = await create.mutateAsync({ batch, childIds: [] });
    onResult?.(res.results?.[0]);
    onSuccess?.();
  };

  // Focus the first interactive control on mount.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const first = root.querySelector<HTMLElement>(
      'button[role="combobox"]:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled])',
    );
    first?.focus();
  }, []);

  // Cmd/Ctrl+Enter submits the form from anywhere within it.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      const form = containerRef.current?.querySelector('form');
      if (form?.requestSubmit) {
        form.requestSubmit();
      } else {
        containerRef.current?.querySelector<HTMLButtonElement>('button[type="submit"]')?.click();
      }
    }
  };

  return (
    <div className="space-y-4" ref={containerRef} onKeyDown={handleKeyDown}>
      <BaseEditableForm<FormValues>
        schema={schema}
        fields={fields}
        groups={groups}
        defaultValues={defaultValues}
        mode="create"
        onSubmit={handleSubmit}
        onCancel={onCancel}
        isSubmitting={create.isPending}
      />
    </div>
  );
}
